import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "utils/AuthContext"; // Add centralized auth
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { firebaseAuth, APP_BASE_PATH } from "app";
import {
  Calendar,
  ClipboardList,
  CalendarDays,
  AlertTriangle,
  MessageSquare,
  FileText,
  Users,
  LogOut,
} from "lucide-react";
import { useWorkingHoursStore } from "utils/workingHoursStore";

// Define the structure for a navigation link
interface NavLink {
  path: string;
  label: string;
  icon: React.ElementType;
  roles: Array<"staff" | "admin" | "guardian" | "all">;
}



const navLinks: NavLink[] = [
  {
    path: "/guardian",
    label: "My Portal",
    icon: Users,
    roles: ["guardian"],
  },
  {
    path: "/manage-guardians",
    label: "Guardians",
    icon: Users,
    roles: ["admin"],
  },
  {
    path: "/chat",
    label: "Chat",
    icon: MessageSquare,
    roles: ["admin", "staff"],
  },
  {
    path: "/staff-job-board",
    label: "Job Board",
    icon: ClipboardList,
    roles: ["staff", "admin"],
  },
  {
    path: "/staff-timesheet",
    label: "Timesheets",
    icon: FileText,
    roles: ["staff", "admin"],
  },
  {
    path: "/office-tasks",
    label: "Office Tasks",
    icon: CalendarDays,
    roles: ["admin", "staff"],
  },
  {
    path: "/diary",
    label: "Diary",
    icon: Calendar,
    roles: ["staff", "admin"],
  },
  {
    path: "/staff-diary",
    label: "Staff Month",
    icon: CalendarDays,
    roles: ["staff", "admin"],
  },
  {
    path: "/incident-report-page",
    label: "Incidents",
    icon: AlertTriangle,
    roles: ["admin", "staff"],
  },
];

export function Sidebar() { // Remove user prop
  const navigate = useNavigate();

  // Get current user info from centralized auth
  const { user, role, loading, error } = useAuth();
  const { clearAllWorkingHours } = useWorkingHoursStore();

  const handleLogout = async () => {
    try {
      // Clear all working hours listeners before signing out
      clearAllWorkingHours();
      
      await firebaseAuth.signOut();
      // Navigate to login or home page after logout
      navigate(`${APP_BASE_PATH}/Login`); // Adjust if your login path is different
    } catch (error) {

      // Optionally show a toast notification for logout failure
    }
  };

  // Handle loading state for role fetching
  if (loading) {
    return (
      <div className="w-64 border-r p-4">
        <p>Loading navigation...</p>
      </div>
    );
  }

  // Handle error state (optional: provide more specific feedback)
  if (error) {
    return (
      <div className="w-64 border-r p-4 text-red-500">
        <p>Error loading user role.</p>
        <Button onClick={handleLogout} variant="destructive" className="w-full mt-4">
          Logout
        </Button>
      </div>
    );
  }

  // Filter links based on role (only after auth state is resolved)
  const filteredLinks = navLinks.filter(
    (link) => link.roles.includes("all") || (role && link.roles.includes(role as any)),
  );

  return (
    <div className="flex flex-col h-full w-64 border-r bg-gray-50 dark:bg-gray-800">
      <div className="p-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
          Auslan Place
        </h2>
        {/* Display User Name if available */}
        {!loading && user && user.displayName && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
            {user.displayName}
          </p>
        )}
      </div>
      <Separator />
      <nav className="flex-grow p-4 space-y-2">
        {filteredLinks.map((link) => (
          <Button
            key={link.path}
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
            onClick={() => navigate(`${APP_BASE_PATH}${link.path}`)}
          >
            <link.icon className="mr-2 h-4 w-4" />
            {link.label}
          </Button>
        ))}
      </nav>
      <Separator />
      <div className="p-4">
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full justify-start text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700 dark:border-gray-600"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );
}
